/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Suspense, lazy } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { StudyMaterialProvider } from './context/StudyMaterialContext';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AlertCircle } from 'lucide-react';
import { CartSuccessModal } from './components/CartSuccessModal';
import { getOrCreateVisitorId } from './utils/visitorTracker';

// Static Page Import for Initial Screen
import { Home } from './pages/Home';

// Lazy-Loaded Page Components
const CourseDetails = lazy(() => import('./pages/CourseDetails').then(m => ({ default: m.CourseDetails })));
const VideoLesson = lazy(() => import('./pages/VideoLesson').then(m => ({ default: m.VideoLesson })));
const PracticeArena = lazy(() => import('./pages/PracticeArena').then(m => ({ default: m.PracticeArena })));
const QuizPlayer = lazy(() => import('./pages/QuizPlayer').then(m => ({ default: m.QuizPlayer })));
const TestResults = lazy(() => import('./pages/TestResults').then(m => ({ default: m.TestResults })));
const Leaderboard = lazy(() => import('./pages/Leaderboard').then(m => ({ default: m.Leaderboard })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPage })));
const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const AboutAndContact = lazy(() => import('./pages/AboutAndContact').then(m => ({ default: m.AboutAndContact })));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage').then(m => ({ default: m.FeedbackPage })));
const Auth = lazy(() => import('./pages/Auth').then(m => ({ default: m.Auth })));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard').then(m => ({ default: m.StudentDashboard })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminPortal = lazy(() => import('./pages/AdminPortal').then(m => ({ default: m.AdminPortal })));
const ProfileAndSettings = lazy(() => import('./pages/ProfileAndSettings').then(m => ({ default: m.ProfileAndSettings })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({ default: m.RefundPolicy })));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy').then(m => ({ default: m.ShippingPolicy })));
const AboutUs = lazy(() => import('./pages/AboutUs').then(m => ({ default: m.AboutUs })));
const Disclaimer = lazy(() => import('./pages/Disclaimer').then(m => ({ default: m.Disclaimer })));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({ default: m.TermsOfService })));
const Welcome = lazy(() => import('./pages/Welcome').then(m => ({ default: m.Welcome })));
const LoginLoading = lazy(() => import('./pages/LoginLoading').then(m => ({ default: m.LoginLoading })));
const Courses = lazy(() => import('./pages/Courses').then(m => ({ default: m.Courses })));
const PremiumCourseDetails = lazy(() => import('./pages/PremiumCourseDetails').then(m => ({ default: m.PremiumCourseDetails })));
const Cart = lazy(() => import('./pages/Cart').then(m => ({ default: m.Cart })));
const ChapterPdfLibrary = lazy(() => import('./pages/ChapterPdfLibrary').then(m => ({ default: m.ChapterPdfLibrary })));
const AdminLoginModal = lazy(() => import('./components/AdminLoginModal').then(m => ({ default: m.AdminLoginModal })));
const StudyMaterialLibrary = lazy(() => import('./pages/StudyMaterialLibrary').then(m => ({ default: m.StudyMaterialLibrary })));
const TodayAim = lazy(() => import('./pages/TodayAim').then(m => ({ default: m.TodayAim })));

const PageLoadingFallback: React.FC = () => (
  <div className="min-h-[50vh] flex items-center justify-center p-8">
    <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Main Router Content Wrapper
const RouterContent: React.FC = () => {
  const { activePage, user, setPage, authModalOpen, setAuthModalOpen, adminLoginModalOpen, logout, setPendingPurchaseAction } = useApp();
  const [showForceLogoutAlert, setShowForceLogoutAlert] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('force_logout=true')) {
      setShowForceLogoutAlert(true);
      // Clean up query param so refreshing does not trigger the alert again
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  React.useEffect(() => {
    const pageTitles: { [key: string]: string } = {
      home: 'Academic Class 6-8 Practice, MCQs & Mock Tests',
      'today-aim': 'Today Aim - Quick Access Navigation',
      courses: 'Courses',
      'premium-course-details': 'NCERT Class 7 Science',
      cart: 'Shopping Cart',
      'course-details': 'Course Details',
      'video-lesson': 'Video Lesson',
      practice: 'Practise Hub',
      'quiz-player': 'Quiz Player',
      results: 'Test Results',
      leaderboard: 'Global Leaderboard',
      blog: 'Blog',
      faq: 'FAQ',
      'help-center': 'Help Center',
      contact: 'Contact Us',
      feedback: 'Feedback',
      login: 'Login',
      signup: 'Sign Up',
      'forgot-password': 'Forgot Password',
      welcome: 'Welcome',
      'login-loading': 'Loading...',
      dashboard: 'Dashboard',
      'study-material': 'Study Material Library',
      'admin-dashboard': 'Admin Dashboard',
      'admin-portal': 'Admin Portal',
      profile: 'Profile & Settings',
      privacy: 'Privacy Policy',
      'refund-policy': 'Cancellation & Refund Policy',
      'shipping-policy': 'Shipping & Delivery Policy',
      about: 'About Us',
      disclaimer: 'Disclaimer',
      terms: 'Terms of Service',
      'terms-of-service': 'Terms of Service',
    };

    const titleSuffix = pageTitles[activePage] || '';
    if (titleSuffix) {
      document.title = `MCQ PULSE™ | ${titleSuffix}`;
    } else {
      document.title = 'MCQ PULSE™';
    }
  }, [activePage]);

  // Record page-view analytics on route change
  React.useEffect(() => {
    try {
      const visitorId = getOrCreateVisitorId();
      if (!sessionStorage.getItem('mcq_pulse_landing_recorded')) {
        fetch('/api/analytics/track-landing', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ visitorId })
        })
          .then(() => {
            sessionStorage.setItem('mcq_pulse_landing_recorded', 'true');
          })
          .catch(() => {});
      }
    } catch (err) {}
  }, []);

  // Record page-view analytics on route change
  React.useEffect(() => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('mcq_pulse_token');

      if (!token) {
        // System A — Anonymous Traffic: Use sessionStorage guard so 1 browsing session = 1 count
        // Navigating Home -> Class -> Chapter -> Study Material or page refresh stays 1 visit
        if (!sessionStorage.getItem('mcq_anon_session_recorded')) {
          fetch('/api/analytics/track', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ isAnonymousSession: true })
          })
            .then(() => {
              sessionStorage.setItem('mcq_anon_session_recorded', '1');
            })
            .catch(() => {});
        }
      } else {
        // System B — Logged-in Student Activity: track route activity for registered user analytics
        const visitorId = getOrCreateVisitorId();
        fetch('/api/analytics/track', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-visitor-id': visitorId,
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ page: activePage, visitorId })
        }).catch(() => {});
      }
    } catch (e) {}
  }, [activePage]);

  // Periodic session heartbeat for active authenticated users (every 2 minutes)
  React.useEffect(() => {
    if (!user) return;
    const sendHeartbeat = () => {
      const token = localStorage.getItem('token') || localStorage.getItem('mcq_pulse_token');
      if (token) {
        fetch('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => {});
      }
    };
    const heartbeatInterval = setInterval(sendHeartbeat, 120000);
    return () => clearInterval(heartbeatInterval);
  }, [user]);

  React.useEffect(() => {
    const protectedPages = ['dashboard', 'admin-dashboard', 'profile', 'video-lesson', 'quiz-player', 'feedback', 'today-aim'];
    
    const checkAuthAndRevalidate = async () => {
      if (protectedPages.includes(activePage)) {
        // If there's no user session in state, redirect immediately
        if (!user) {
          if (activePage === 'admin-dashboard') {
            setPage('admin-portal');
          } else if (activePage === 'feedback') {
            setPage('home');
            setPendingPurchaseAction({ fn: () => setPage('feedback') });
            setAuthModalOpen(true, 'login');
          } else {
            setPage('login');
          }
          return;
        }

        // If there is a user but they try to access admin-dashboard without being an administrator
        if (activePage === 'admin-dashboard' && user.role !== 'admin') {
          setPage('home');
          return;
        }

        // Revalidate token with the backend on every route change of protected page
        const token = localStorage.getItem('token') || localStorage.getItem('mcq_pulse_token');
        if (!token) {
          // If no token exists, the session is invalid
          logout();
          if (activePage === 'admin-dashboard') {
            setPage('admin-portal');
          }
          return;
        }

        try {
          const res = await fetch('/api/auth/verify-session', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (!res.ok) {
            // Session is expired or invalid on the server
            logout();
            if (activePage === 'admin-dashboard') {
              setPage('admin-portal');
            }
          }
        } catch (err) {
          console.error('Session revalidation failed:', err);
        }
      }
    };

    checkAuthAndRevalidate();
  }, [activePage, user, setPage, logout]);

  const renderActiveView = () => {
    switch (activePage) {
      case 'home':
        return user && user.role === 'student' ? <TodayAim /> : <Home />;
      case 'today-aim':
        return <TodayAim />;
      case 'courses':
        return <Courses />;
      case 'premium-course-details':
        return <PremiumCourseDetails />;
      case 'cart':
        return <Cart />;
      case 'pdf-library':
        return <ChapterPdfLibrary />;
      case 'course-details':
        return <CourseDetails />;
      case 'video-lesson':
        return <VideoLesson />;
      case 'practice':
        return <PracticeArena />;
      case 'quiz-player':
        return <QuizPlayer />;
      case 'results':
        return <TestResults />;
      case 'leaderboard':
        return <Leaderboard />;
      case 'blog':
        return <BlogPage />;
      case 'faq':
      case 'help-center':
        return <FAQPage />;
      case 'contact':
        return <AboutAndContact />;
      case 'feedback':
        return <FeedbackPage />;
      case 'login':
      case 'signup':
      case 'forgot-password':
        return <Auth />;
      case 'welcome':
        return <Welcome />;
      case 'login-loading':
        return <LoginLoading />;
      case 'dashboard':
        return <StudentDashboard />;
      case 'study-material':
        return <StudyMaterialLibrary />;
      case 'admin-dashboard':
        return <AdminDashboard />;
      case 'admin-portal':
        return <AdminPortal />;
      case 'profile':
        return <ProfileAndSettings />;
      case 'privacy':
        return <PrivacyPolicy />;
      case 'refund-policy':
        return <RefundPolicy />;
      case 'shipping-policy':
        return <ShippingPolicy />;
      case 'about':
        return <AboutUs />;
      case 'disclaimer':
        return <Disclaimer />;
      case 'terms':
      case 'terms-of-service':
        return <TermsOfService />;
      default:
        return <Home />;
    }
  };

  const isFullscreenView = activePage === 'welcome' || activePage === 'login-loading' || activePage === 'admin-portal';

  return (
    <div className="flex flex-col min-h-screen bg-slate-50 dark:bg-[#090d16] text-slate-900 dark:text-slate-100 transition-colors">
      
      {/* Dynamic Header */}
      {!isFullscreenView && <Header />}

      {/* Main Content Arena */}
      <main className="flex-grow">
        <Suspense fallback={<PageLoadingFallback />}>
          {renderActiveView()}
        </Suspense>
      </main>

      {/* Premium Footer */}
      {!isFullscreenView && <Footer />}

      {/* Global Shopping Cart Added success modal */}
      <CartSuccessModal />

      {/* Global Auth Modal */}
      {authModalOpen && (
        <Suspense fallback={null}>
          <Auth />
        </Suspense>
      )}

      {/* Global Admin Login Modal */}
      {adminLoginModalOpen && (
        <Suspense fallback={null}>
          <AdminLoginModal />
        </Suspense>
      )}

      {/* Force Logout Warning Modal */}
      {showForceLogoutAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-xl shadow-xl overflow-hidden border border-slate-100 dark:border-slate-800">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-50 dark:bg-amber-950/30 text-amber-500 rounded-full">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-center text-slate-900 dark:text-slate-100 mb-2">
                Session Expired
              </h3>
              <p className="text-sm text-center text-slate-500 dark:text-slate-400 leading-relaxed mb-6">
                You have been logged out because your account was accessed from another device. MCQ PULSE™ allows only one active session per student account to ensure academic integrity.
              </p>
              <button
                onClick={() => setShowForceLogoutAlert(false)}
                className="w-full py-2.5 px-4 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-950"
              >
                Got it, Sign In Again
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default function App() {
  return (
    <AppProvider>
      <StudyMaterialProvider>
        <RouterContent />
      </StudyMaterialProvider>
    </AppProvider>
  );
}
